import { useCallback, useEffect, useRef, useState } from 'react'

import {
  faChevronLeft,
  faChevronRight,
  faDownload,
  faImages,
  faTableCells,
  faTimes,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Image from 'next/image'
import { createPortal } from 'react-dom'
import { useIntl } from 'react-intl'

import { EmbeddedVideo } from '@b-stock/fe-core/bstock-next/components'
import { useVideoAutostop } from '@b-stock/fe-core/bstock-next/helpers'
import { useBodyScroll } from '@b-stock/fe-core/bstock-react'

import { getLegacyBpStaticPath } from '@/buyer-portal/buyUrlPrefix'
import { useVideoTracking } from '@/helpers/video/useVideoTracking'

import type { NavMethod } from '../../shared/useGalleryTracking'
import { useGalleryTracking } from '../../shared/useGalleryTracking'
import { MediaGrid } from '../MediaGrid'
import type { KeyedMedia } from '../types'
import { useBulkDownload } from '../useBulkDownload'
import { DotStrip } from './DotStrip'
import {
  CloseButton,
  Counter,
  Dialog,
  DownloadButton,
  Footer,
  GridToggleButton,
  Header,
  HeaderActions,
  ImageArea,
  ImageWrapper,
  NavButtonLeft,
  NavButtonRight,
  Overlay,
  Title,
} from './ui'
import { useLightboxInput } from './useLightboxInput'

const placeholder = getLegacyBpStaticPath('images/placeholder-image.png')
const noop = () => {}

const LightboxContent = ({
  bulkDownloadEnabled,
  gridViewEnabled,
  initialIndex,
  items,
  onClose,
  title,
}: {
  bulkDownloadEnabled?: boolean
  gridViewEnabled?: boolean
  initialIndex: number
  items: KeyedMedia[]
  onClose: () => void
  title: string
}) => {
  useBodyScroll(false)

  const { formatMessage } = useIntl()
  const dialogRef = useRef<HTMLDivElement>(null)
  const videoRefsMap = useRef<Map<number, HTMLIFrameElement>>(new Map())
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [viewMode, setViewMode] = useState<'carousel' | 'grid'>('carousel')

  const total = items.length
  const currentItem = items[currentIndex]
  const currentIsVideo = currentItem?.type === 'video'

  const { downloading, downloadAll } = useBulkDownload(items)

  const { analyticsData, trackNavigate } = useGalleryTracking(items)

  useVideoAutostop(videoRefsMap, currentIndex, items)
  useVideoTracking({ analyticsData, media: items, videoRefsMap })

  const navigate = useCallback(
    (index: number, navMethod: NavMethod) => {
      const nextIndex = (index + total) % total
      if (nextIndex === currentIndex) return
      setCurrentIndex(nextIndex)
      trackNavigate(nextIndex, 'fullscreen', navMethod)
    },
    [currentIndex, total, trackNavigate]
  )
  const goTo = useCallback(
    (index: number) => navigate(index, 'jump'),
    [navigate]
  )
  const goPrev = useCallback(
    () => navigate(currentIndex - 1, 'step'),
    [currentIndex, navigate]
  )
  const goNext = useCallback(
    () => navigate(currentIndex + 1, 'step'),
    [currentIndex, navigate]
  )

  // Focus dialog on mount for keyboard access
  useEffect(() => {
    dialogRef.current?.focus()
  }, [])

  const isCarouselShown = viewMode === 'carousel'
  const { onTouchStart, onTouchEnd } = useLightboxInput({
    onPrev: isCarouselShown ? goPrev : noop,
    onNext: isCarouselShown ? goNext : noop,
    onClose,
  })

  return (
    <>
      <Overlay onClick={onClose} />
      <Dialog
        aria-label={title}
        aria-modal
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
        onTouchEnd={onTouchEnd}
        onTouchStart={onTouchStart}
      >
        <Header>
          <Title>{title}</Title>
          <HeaderActions>
            {bulkDownloadEnabled && (
              <DownloadButton
                aria-label={formatMessage({
                  id: downloading
                    ? 'Auction.details.enhancedGallery.lightbox.downloading'
                    : 'Auction.details.enhancedGallery.lightbox.downloadAll',
                })}
                disabled={downloading}
                onClick={downloadAll}
              >
                <FontAwesomeIcon icon={faDownload} />
              </DownloadButton>
            )}
            {gridViewEnabled && (
              <GridToggleButton
                aria-label={formatMessage({
                  id:
                    viewMode === 'carousel'
                      ? 'Auction.details.enhancedGallery.lightbox.toggleGrid'
                      : 'Auction.details.enhancedGallery.lightbox.toggleCarousel',
                })}
                onClick={() =>
                  setViewMode((m) => (m === 'carousel' ? 'grid' : 'carousel'))
                }
              >
                <FontAwesomeIcon
                  icon={viewMode === 'carousel' ? faTableCells : faImages}
                />
              </GridToggleButton>
            )}
            <CloseButton
              aria-label={formatMessage({
                id: 'Auction.details.enhancedGallery.lightbox.close',
              })}
              onClick={onClose}
            >
              <FontAwesomeIcon icon={faTimes} />
            </CloseButton>
          </HeaderActions>
        </Header>

        {viewMode === 'grid' && gridViewEnabled ? (
          <MediaGrid
            items={items}
            onSelect={(index) => {
              goTo(index)
              setViewMode('carousel')
            }}
          />
        ) : null}

        {viewMode === 'carousel' && (
          <>
            <ImageArea>
              {total > 1 && (
                <NavButtonLeft
                  aria-label={formatMessage({
                    id: 'Auction.details.enhancedGallery.lightbox.previous',
                  })}
                  onClick={goPrev}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </NavButtonLeft>
              )}

              <ImageWrapper>
                {currentItem &&
                  (currentIsVideo ? (
                    <EmbeddedVideo
                      key={currentItem.key}
                      onRef={(el) => {
                        if (!el) return
                        videoRefsMap.current.set(currentIndex, el)
                        return () => videoRefsMap.current.delete(currentIndex)
                      }}
                      title={currentItem.title}
                      videoData={currentItem.videoData}
                    />
                  ) : (
                    <Image
                      key={currentItem.key}
                      alt={
                        currentItem.title ??
                        formatMessage(
                          {
                            id: 'Auction.details.enhancedGallery.thumbnailAlt',
                          },
                          { index: currentIndex + 1 }
                        )
                      }
                      fill
                      priority
                      src={
                        currentItem.urls?.large ??
                        currentItem.urls?.medium ??
                        placeholder
                      }
                      style={{ objectFit: 'contain' }}
                    />
                  ))}
              </ImageWrapper>

              {total > 1 && (
                <NavButtonRight
                  aria-label={formatMessage({
                    id: 'Auction.details.enhancedGallery.lightbox.next',
                  })}
                  onClick={goNext}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </NavButtonRight>
              )}
            </ImageArea>

            <Footer>
              {total > 1 && (
                <DotStrip
                  items={items}
                  currentIndex={currentIndex}
                  onDotClick={goTo}
                />
              )}
              <Counter>
                {formatMessage(
                  { id: 'Auction.details.enhancedGallery.lightbox.counter' },
                  { current: currentIndex + 1, total }
                )}
              </Counter>
            </Footer>
          </>
        )}
      </Dialog>
    </>
  )
}

export const MediaLightbox = (
  props: React.ComponentPropsWithoutRef<typeof LightboxContent>
) => createPortal(<LightboxContent {...props} />, document.body)
